/**
 * MonthlyTimeline - Horizontal timeline showing inspections, citations, and penalties
 *
 * Displays a visual timeline from 2019-present with:
 * - Inspection events (health/complaint)
 * - Citation severity indicators
 * - Penalty amounts
 * - Core staffing violation markers
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  LocalHospital as HealthIcon,
  Report as ComplaintIcon,
  AttachMoney as PenaltyIcon,
  Description as DocumentIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Visibility as PreviewIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import api from '../../api/axios';
import {
  SEVERITY_INFO,
  getSeverityColor,
  getSeverityBgColor,
  UNDERSTAFFING_CORE,
  UNDERSTAFFING_INDICATORS,
  FTAG_CATEGORIES,
} from '../../types/inspections';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

interface CitationDetail {
  ftag: string;
  description: string;
  severity: string;
  is_harm: boolean;
  is_staffing: boolean;
  is_understaffing_core: boolean;
  is_understaffing_indicator: boolean;
  is_abuse: boolean;
  category: string;
}

interface InspectionEvent {
  id: string;
  date: string;
  type: 'HEALTH' | 'COMPLAINT' | 'COMBINED';
  total_citations: number;
  harm_citations: number;
  jeopardy_citations: number;
  staffing_citations: number;
  has_staffing_citation: boolean;
  has_abuse_citation: boolean;
  core_staffing_ftags: string[];
  harm_ftags: { ftag: string; severity: string }[];
  has_report: boolean;
  citations?: CitationDetail[];
}

interface PenaltyEvent {
  date: string;
  type: string;
  fine_amount: number;
  payment_denial_start: string | null;
  payment_denial_days: number | null;
}

interface MonthData {
  month: string;
  year: number;
  inspections: InspectionEvent[];
  penalties: PenaltyEvent[];
  totals: {
    health_inspections: number;
    complaint_inspections: number;
    total_citations: number;
    harm_citations: number;
    jeopardy_citations: number;
    staffing_citations: number;
    total_fines: number;
  };
}

interface MonthlyTimelineProps {
  ccn: string;
  schemeId?: string;
  startYear?: number;
  endYear?: number;
  hideChart?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const formatMoney = (val: number): string => {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${Math.round(val / 1000)}K`;
  return `$${val.toLocaleString()}`;
};

const getMonthLabel = (month: string): string => {
  const [_year, m] = month.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return monthNames[parseInt(m, 10) - 1];
};

// Generate all months between start and end year
const generateAllMonths = (startYear: number, endYear: number): string[] => {
  const months: string[] = [];
  for (let year = startYear; year <= endYear; year++) {
    for (let month = 1; month <= 12; month++) {
      const monthStr = `${year}-${month.toString().padStart(2, '0')}`;
      // Don't include future months
      const now = new Date();
      const [y, m] = monthStr.split('-').map(Number);
      if (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth() + 1)) {
        months.push(monthStr);
      }
    }
  }
  return months;
};

// Cell width constant for timeline
const CELL_WIDTH = 36;

// Change this set to control which citations appear on the worst-citations timeline
const WORST_CITATION_SEVERITIES = new Set(['G', 'H', 'I', 'J', 'K', 'L']);

// Scope label from severity code position in the A-L grid
const getScopeFromSeverity = (severity: string): 'isolated' | 'pattern' | 'widespread' => {
  if ('ADG J'.includes(severity)) return 'isolated';
  if ('BEH K'.includes(severity)) return 'pattern';
  return 'widespread';
};

// ─────────────────────────────────────────────────────────────────────────────
// WORST CITATIONS STRIP
// ─────────────────────────────────────────────────────────────────────────────

interface WorstCitation {
  date: string;
  ftag: string;
  severity: string;
  inspectionId: string;
  scope: 'isolated' | 'pattern' | 'widespread';
  isUnderstaffing: boolean;
  isAbuse: boolean;
}

interface WorstCitationsStripProps {
  timelineData: MonthData[];
  startYear: number;
  endYear: number;
  onCitationClick: (inspectionId: string, ftag: string) => void;
}

const WorstCitationsStrip: React.FC<WorstCitationsStripProps> = ({
  timelineData,
  startYear,
  endYear,
  onCitationClick,
}) => {
  // Collect notable citations: harm/jeopardy severity OR understaffing/abuse flagged
  const worstCitations: WorstCitation[] = [];
  timelineData.forEach((monthData) => {
    monthData.inspections.forEach((insp) => {
      if (insp.citations) {
        insp.citations.forEach((cit) => {
          const isUnderstaffing = cit.is_understaffing_core || cit.is_understaffing_indicator || cit.is_staffing;
          const isAbuse = cit.is_abuse;
          const isWorstSeverity = WORST_CITATION_SEVERITIES.has(cit.severity);
          if (isWorstSeverity || isUnderstaffing || isAbuse) {
            worstCitations.push({
              date: insp.date,
              ftag: cit.ftag,
              severity: cit.severity,
              inspectionId: insp.id || insp.date,
              scope: getScopeFromSeverity(cit.severity),
              isUnderstaffing,
              isAbuse,
            });
          }
        });
      }
    });
  });

  if (worstCitations.length === 0) return null;

  // Deduplicate by date+ftag (keep worst severity, preserve flags)
  const byKey = new Map<string, WorstCitation>();
  worstCitations.forEach((c) => {
    const key = `${c.date}-${c.ftag}`;
    const existing = byKey.get(key);
    if (!existing || c.severity > existing.severity) {
      byKey.set(key, c);
    } else if (existing) {
      // Merge flags from duplicates
      existing.isUnderstaffing = existing.isUnderstaffing || c.isUnderstaffing;
      existing.isAbuse = existing.isAbuse || c.isAbuse;
    }
  });

  // Group by date for clustered display
  const byDate = new Map<string, WorstCitation[]>();
  Array.from(byKey.values()).forEach((c) => {
    if (!byDate.has(c.date)) byDate.set(c.date, []);
    byDate.get(c.date)!.push(c);
  });
  const dateGroups = Array.from(byDate.entries())
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime());

  // Proportional positioning within container
  const rangeStartMs = new Date(`${startYear}-01-01`).getTime();
  const rangeEndMs = new Date(`${endYear}-12-31`).getTime();
  const totalMs = rangeEndMs - rangeStartMs;
  const getPercent = (dateStr: string) =>
    ((new Date(dateStr).getTime() - rangeStartMs) / totalMs) * 100;

  // Year tick positions
  const years: number[] = [];
  for (let y = startYear; y <= endYear; y++) years.push(y);

  // Height scales with max group size
  const maxGroupSize = Math.max(...dateGroups.map(([, cits]) => cits.length));
  const CHIP_HEIGHT = 18;
  const CHIP_GAP = 3;
  const AXIS_Y = 16; // axis near top
  const STRIP_HEIGHT = Math.max(90, AXIS_Y + maxGroupSize * (CHIP_HEIGHT + CHIP_GAP) + 12);

  return (
    <Box
      sx={{
        backgroundColor: '#fafafa',
        borderRadius: '8px 8px 0 0',
        border: '1px solid',
        borderColor: 'divider',
        borderBottom: 'none',
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 4,
            height: 18,
            borderRadius: 1,
            background: 'linear-gradient(to bottom, #d32f2f, #7b1fa2)',
          }}
        />
        <Typography variant="subtitle2" fontWeight={700} color="text.primary">
          Notable Citations
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Understaffing, abuse & harm citations — click to jump to inspection
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: '#fff3e0', border: '1.5px solid #ed6c02' }} />
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#ed6c02', fontWeight: 600 }}>Understaffing</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: '#ffebee', border: '1.5px solid #d32f2f' }} />
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#d32f2f', fontWeight: 600 }}>Actual Harm</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: '#f3e5f5', border: '1.5px solid #7b1fa2' }} />
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#7b1fa2', fontWeight: 600 }}>Imm. Jeopardy</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: 'white', border: '1.5px dashed #d32f2f' }} />
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#d32f2f', fontWeight: 600 }}>Abuse</Typography>
          </Box>
        </Box>
      </Box>

      {/* Timeline strip */}
      <Box sx={{ px: 2, pb: 1.5 }}>
        <Box sx={{ position: 'relative', height: STRIP_HEIGHT }}>
          {/* Year ticks and labels */}
          {years.map((year) => {
            const pct = getPercent(`${year}-01-01`);
            return (
              <React.Fragment key={year}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${pct}%`,
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    backgroundColor: 'grey.200',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    left: `${pct}%`,
                    top: AXIS_Y - 14,
                    transform: 'translateX(2px)',
                    fontSize: '0.6rem',
                    color: 'text.disabled',
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  {year}
                </Typography>
              </React.Fragment>
            );
          })}

          {/* Horizontal axis line */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: AXIS_Y,
              height: '2px',
              backgroundColor: 'grey.300',
              borderRadius: 1,
            }}
          />

          {/* Date groups — vertical stacks hanging below the axis */}
          {dateGroups.map(([date, cits]) => {
            const pct = getPercent(date);

            return (
              <Box
                key={date}
                sx={{
                  position: 'absolute',
                  left: `${pct}%`,
                  top: AXIS_Y - 3,
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  zIndex: 2,
                }}
              >
                {/* Dot on the axis */}
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'grey.500', border: '2px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' }} />

                {/* Stacked F-tag chips */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${CHIP_GAP}px`, mt: 0.5 }}>
                  {cits.map((cit, citIdx) => {
                    const isJeopardy = ['J', 'K', 'L'].includes(cit.severity);
                    const isHarmSev = ['G', 'H', 'I'].includes(cit.severity);

                    let chipColor = '#ed6c02';
                    let chipBg = '#fff3e0';
                    if (isJeopardy) { chipColor = '#7b1fa2'; chipBg = '#f3e5f5'; }
                    else if (isHarmSev) { chipColor = '#d32f2f'; chipBg = '#ffebee'; }

                    const isAbuseOnly = cit.isAbuse && !cit.isUnderstaffing && !isHarmSev && !isJeopardy;
                    const scopeCount = cit.scope === 'isolated' ? 1 : cit.scope === 'pattern' ? 2 : 3;

                    const flags = [
                      cit.isUnderstaffing && 'Understaffing',
                      cit.isAbuse && 'Abuse',
                    ].filter(Boolean).join(', ');
                    const tooltipText = `${cit.ftag} (${cit.severity}) — ${SEVERITY_INFO[cit.severity]?.label || ''}${flags ? ` — ${flags}` : ''} — ${new Date(cit.date).toLocaleDateString()}`;

                    return (
                      <Tooltip key={`${cit.ftag}-${citIdx}`} title={tooltipText} arrow>
                        <Box
                          onClick={() => onCitationClick(cit.inspectionId, cit.ftag)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px',
                            backgroundColor: isAbuseOnly ? 'white' : chipBg,
                            border: `1.5px solid ${chipColor}`,
                            borderStyle: isAbuseOnly ? 'dashed' : 'solid',
                            borderRadius: 1,
                            px: 0.75,
                            py: 0.25,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            height: CHIP_HEIGHT,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            transition: 'transform 0.1s, box-shadow 0.1s',
                            '&:hover': {
                              transform: 'scale(1.1)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              zIndex: 10,
                            },
                          }}
                        >
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: chipColor, lineHeight: 1 }}>
                            {cit.ftag}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: '1.5px' }}>
                            {Array.from({ length: scopeCount }).map((_, i) => (
                              <Box
                                key={i}
                                sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: chipColor, opacity: 0.7 }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SEVERITY LEGEND
// ─────────────────────────────────────────────────────────────────────────────

// CMS F-tag descriptions (from 42 CFR § 483)
const FTAG_INFO: Record<string, { name: string; description: string }> = {
  F600: { name: 'Free from Abuse/Neglect', description: 'Protect each resident from all types of abuse such as physical, mental, sexual abuse, physical punishment, and neglect.' },
  F602: { name: 'Misappropriation of Property', description: 'Protect each resident from the wrongful use of the resident\'s belongings or money.' },
  F603: { name: 'Free from Involuntary Seclusion', description: 'Protect each resident from separation from other residents, his/her room, or confinement to his/her room.' },
  F604: { name: 'Right to be Free from Physical Restraints', description: 'Ensure that each resident is free from the use of physical restraints, unless needed for medical treatment.' },
  F605: { name: 'Right to be Free from Chemical Restraints', description: 'Ensure that each resident is free from medications that restrain them, unless needed for medical treatment.' },
  F606: { name: 'Not Employ Persons with Adverse Actions', description: 'Not hire anyone with a finding of abuse, neglect, exploitation, or theft.' },
  F607: { name: 'Develop/Implement Abuse Policies', description: 'Develop and implement policies and procedures to prevent abuse, neglect, and theft.' },
  F608: { name: 'Reporting of Crimes', description: 'Ensure employees report any suspicion of a crime against any resident according to timelines; post notice of employee rights; prohibit retaliation.' },
  F609: { name: 'Timely Reporting of Allegations', description: 'Timely report suspected abuse, neglect, or theft and report the results of the investigation to proper authorities.' },
  F610: { name: 'Investigate/Respond to Allegations', description: 'Respond appropriately to all alleged violations.' },
  F684: { name: 'Quality of Care', description: 'Provide appropriate treatment and care according to orders, resident\'s preferences and goals.' },
  F686: { name: 'Pressure Ulcer Treatment/Prevention', description: 'Provide appropriate pressure ulcer care and prevent new ulcers from developing.' },
  F689: { name: 'Free from Accident Hazards', description: 'Ensure that the nursing home area is free from accident hazards and provides adequate supervision to prevent accidents.' },
  F690: { name: 'Bowel/Bladder Incontinence Care', description: 'Provide appropriate care for residents who are continent or incontinent of bowel/bladder, appropriate catheter care, and care to prevent urinary tract infections.' },
  F692: { name: 'Nutrition/Hydration Status', description: 'Provide enough food/fluids to maintain a resident\'s health.' },
  F725: { name: 'Sufficient Nursing Staff', description: 'Provide enough nursing staff every day to meet the needs of every resident; have a licensed nurse in charge on each shift.' },
  F726: { name: 'Competent Nursing Staff', description: 'Ensure that nurses and nurse aides have the appropriate competencies to care for every resident in a way that maximizes each resident\'s well being.' },
  F727: { name: 'RN 8 Hours/7 Days', description: 'Have a registered nurse on duty 8 hours a day; select a registered nurse to be the director of nurses on a full time basis.' },
  F757: { name: 'Drug Regimen Free from Unnecessary Drugs', description: 'Ensure each resident\'s drug regimen must be free from unnecessary drugs.' },
  F758: { name: 'Psychotropic Medication Use', description: 'Implement gradual dose reductions and non-pharmacological interventions, unless contraindicated, prior to initiating or instead of continuing psychotropic medication.' },
  F759: { name: 'Medication Error Rate', description: 'Ensure medication error rates are not 5 percent or greater.' },
  F851: { name: 'Payroll-Based Journal', description: 'Electronically submit to CMS complete and accurate direct care staffing information, based on payroll and other verifiable and auditable data.' },
  F880: { name: 'Infection Prevention & Control', description: 'Provide and implement an infection prevention and control program.' },
};

const SeverityLegend: React.FC = () => {
  const [expanded, setExpanded] = useState(true);
  const [severityDialogOpen, setSeverityDialogOpen] = useState(false);
  const [flagsDialogOpen, setFlagsDialogOpen] = useState(false);

  const gridRows = [
    { harm: 'No Actual Harm', codes: ['A', 'B', 'C'], color: '#2e7d32', bgColor: '#e8f5e9' },
    { harm: 'Potential Harm', codes: ['D', 'E', 'F'], color: '#ed6c02', bgColor: '#fff3e0' },
    { harm: 'Actual Harm', codes: ['G', 'H', 'I'], color: '#d32f2f', bgColor: '#ffebee' },
    { harm: 'Imm. Jeopardy', codes: ['J', 'K', 'L'], color: '#7b1fa2', bgColor: '#f3e5f5' },
  ];

  // Combine core + indicator into one deduplicated, sorted list
  const allUnderstaffingFtags = Array.from(new Set([...UNDERSTAFFING_CORE, ...UNDERSTAFFING_INDICATORS])).sort();

  const flagDefs = [
    {
      label: 'Understaffing',
      color: '#ed6c02',
      variant: 'filled' as const,
      description: 'Core understaffing citation — direct staffing regulation violations',
      ftags: UNDERSTAFFING_CORE.join(', '),
    },
    {
      label: 'Staffing-Related',
      color: '#ef6c00',
      variant: 'outlined' as const,
      description: 'Staffing-related citation — conditions commonly caused or exacerbated by understaffing',
      ftags: UNDERSTAFFING_INDICATORS.join(', '),
    },
    {
      label: 'Abuse',
      color: '#d32f2f',
      variant: 'outlined' as const,
      description: 'Abuse/Neglect citation',
      ftags: FTAG_CATEGORIES.abuse_neglect.join(', '),
    },
  ];

  const understaffingCoreFtags = ['F725', 'F726', 'F727', 'F851'];
  const understaffingIndicatorFtags = ['F600', 'F684', 'F686', 'F689', 'F690', 'F692', 'F757', 'F758', 'F759', 'F880'];
  const abuseFtags = FTAG_CATEGORIES.abuse_neglect; // F600, F602-F610

  return (
    <Box
      sx={{
        mb: 1.5,
        backgroundColor: 'grey.50',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Collapsed header — always visible */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'grey.100' },
          borderRadius: '8px 8px 0 0',
          transition: 'background-color 0.15s',
        }}
      >
        <Typography variant="body2" fontWeight={700} color="text.primary">
          Inspection Key
        </Typography>
        <ExpandMoreIcon
          sx={{
            ml: 'auto',
            fontSize: 18,
            color: 'text.secondary',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </Box>

      {/* Expanded content */}
      <Collapse in={expanded}>
        <Box
          sx={{
            px: 2,
            pb: 2,
            pt: 0.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 3,
            alignItems: 'flex-start',
          }}
        >
          {/* Severity Grid */}
          <Box sx={{ flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                CMS Severity Codes (Scope x Harm)
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setSeverityDialogOpen(true); }}
                sx={{ p: 0.25 }}
              >
                <InfoIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              </IconButton>
            </Box>
            <Table size="small" sx={{ maxWidth: 360 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, py: 0.5, px: 1, fontSize: '0.7rem', width: 100 }}></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 0.5, px: 1, fontSize: '0.7rem' }}>Isolated</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 0.5, px: 1, fontSize: '0.7rem' }}>Pattern</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, py: 0.5, px: 1, fontSize: '0.7rem' }}>Widespread</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gridRows.map((row) => (
                  <TableRow key={row.harm}>
                    <TableCell sx={{ py: 0.5, px: 1, fontWeight: 600, fontSize: '0.7rem', color: row.color }}>
                      {row.harm}
                    </TableCell>
                    {row.codes.map((code) => (
                      <TableCell key={code} align="center" sx={{ py: 0.5, px: 1 }}>
                        <Chip
                          size="small"
                          label={code}
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            height: 22,
                            minWidth: 28,
                            backgroundColor: row.bgColor,
                            color: row.color,
                            border: `1.5px solid ${row.color}`,
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Vertical divider */}
          <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'grey.300', flexShrink: 0 }} />

          {/* Flag definitions */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                Citation Flags
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setFlagsDialogOpen(true); }}
                sx={{ p: 0.25 }}
              >
                <InfoIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {flagDefs.map((flag) => (
                <Box key={flag.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Chip
                    size="small"
                    label={flag.label}
                    variant={flag.variant}
                    sx={{
                      fontSize: '0.65rem',
                      height: 20,
                      minWidth: 85,
                      flexShrink: 0,
                      mt: 0.125,
                      ...(flag.variant === 'filled'
                        ? { backgroundColor: flag.color, color: 'white' }
                        : { borderColor: flag.color, color: flag.color }),
                    }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {flag.description}
                    </Typography>
                    {flag.ftags && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: '0.6rem', mt: 0.25 }}>
                        {flag.ftags}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Collapse>

      {/* Severity Codes Info Dialog */}
      <Dialog
        open={severityDialogOpen}
        onClose={() => setSeverityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>CMS Scope & Severity Grid</Typography>
          <IconButton size="small" onClick={() => setSeverityDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            CMS surveyors assign each citation a <strong>Scope & Severity code</strong> (A through L) based on two dimensions:
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Severity (Harm Level)</Typography>
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 2 }}>
            <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
              <li><Box component="span" sx={{ color: '#2e7d32', fontWeight: 600 }}>No Actual Harm (A-C)</Box> — Minimal potential for harm. The deficiency has the potential for no more than minor negative impact on the resident.</li>
              <li><Box component="span" sx={{ color: '#ed6c02', fontWeight: 600 }}>Potential Harm (D-F)</Box> — No actual harm but potential for more than minimal harm. The deficiency creates a reasonable possibility of more than minimal impact.</li>
              <li><Box component="span" sx={{ color: '#d32f2f', fontWeight: 600 }}>Actual Harm (G-I)</Box> — Residents experienced actual harm that is not immediate jeopardy. This includes compromised ability to maintain or reach highest well-being.</li>
              <li><Box component="span" sx={{ color: '#7b1fa2', fontWeight: 600 }}>Immediate Jeopardy (J-L)</Box> — Serious injury, harm, impairment, or death to a resident has occurred, is occurring, or is likely to occur.</li>
            </Box>
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Scope (How Many Residents Affected)</Typography>
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 2 }}>
            <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
              <li><strong>Isolated</strong> — One or a very limited number of residents affected.</li>
              <li><strong>Pattern</strong> — More than a very limited number of residents affected, or a situation in which the facility has failed to identify or correct a systemic problem.</li>
              <li><strong>Widespread</strong> — Pervasive problem affecting or with potential to affect a large portion of the facility's residents.</li>
            </Box>
          </Typography>

          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, py: 0.75, fontSize: '0.8rem' }}></TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, py: 0.75, fontSize: '0.8rem' }}>Isolated</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, py: 0.75, fontSize: '0.8rem' }}>Pattern</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, py: 0.75, fontSize: '0.8rem' }}>Widespread</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gridRows.map((row) => (
                <TableRow key={row.harm}>
                  <TableCell sx={{ py: 0.75, fontWeight: 600, fontSize: '0.8rem', color: row.color }}>
                    {row.harm}
                  </TableCell>
                  {row.codes.map((code) => (
                    <TableCell key={code} align="center" sx={{ py: 0.75 }}>
                      <Chip
                        size="small"
                        label={code}
                        sx={{
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          height: 26,
                          minWidth: 32,
                          backgroundColor: row.bgColor,
                          color: row.color,
                          border: `1.5px solid ${row.color}`,
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
            Source: CMS State Operations Manual, Appendix P — Survey Protocol for Long-Term Care Facilities
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeverityDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Citation Flags Info Dialog */}
      <Dialog
        open={flagsDialogOpen}
        onClose={() => setFlagsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Citation Flags — Understaffing & Abuse</Typography>
          <IconButton size="small" onClick={() => setFlagsDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Citations are flagged based on the CMS F-tag (federal regulation tag) that was violated.
            These flags help identify patterns of understaffing and abuse/neglect across inspections.
          </Typography>

          {/* Understaffing Core */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                size="small"
                label="Understaffing"
                sx={{ fontSize: '0.7rem', height: 22, backgroundColor: '#ed6c02', color: 'white' }}
              />
              <Typography variant="subtitle2">Core Staffing Violations</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Direct violations of CMS staffing requirements (42 CFR § 483.35, § 483.70(q)).
              These citations indicate the facility failed to meet minimum staffing standards.
            </Typography>
            <Table size="small" sx={{ '& td, & th': { py: 0.75, px: 1.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 70, fontSize: '0.75rem' }}>F-tag</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 200, fontSize: '0.75rem' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>CMS Requirement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {understaffingCoreFtags.map((ftag) => (
                  <TableRow key={ftag}>
                    <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.75rem' }}>{ftag}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{FTAG_INFO[ftag]?.name}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{FTAG_INFO[ftag]?.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Staffing-Related Indicators */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                size="small"
                label="Staffing-Related"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 22, borderColor: '#ef6c00', color: '#ef6c00' }}
              />
              <Typography variant="subtitle2">Staffing-Related Indicator Conditions</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Conditions commonly caused or exacerbated by insufficient staffing. While not direct staffing
              citations, research shows strong correlation between understaffing and these deficiencies.
            </Typography>
            <Table size="small" sx={{ '& td, & th': { py: 0.75, px: 1.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 70, fontSize: '0.75rem' }}>F-tag</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 200, fontSize: '0.75rem' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>CMS Requirement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {understaffingIndicatorFtags.map((ftag) => (
                  <TableRow key={ftag}>
                    <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.75rem' }}>{ftag}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{FTAG_INFO[ftag]?.name}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{FTAG_INFO[ftag]?.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Abuse / Neglect */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                size="small"
                label="Abuse"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 22, borderColor: '#d32f2f', color: '#d32f2f' }}
              />
              <Typography variant="subtitle2">Abuse & Neglect Violations</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Violations of resident protection requirements under 42 CFR § 483.12.
              These citations indicate the facility failed to protect residents from abuse, neglect, exploitation, or misappropriation of property.
            </Typography>
            <Table size="small" sx={{ '& td, & th': { py: 0.75, px: 1.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 70, fontSize: '0.75rem' }}>F-tag</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 200, fontSize: '0.75rem' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>CMS Requirement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {abuseFtags.map((ftag) => (
                  <TableRow key={ftag}>
                    <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.75rem' }}>{ftag}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{FTAG_INFO[ftag]?.name}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{FTAG_INFO[ftag]?.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
            Source: CMS State Operations Manual, Appendix PP — Guidance to Surveyors for Long-Term Care Facilities (42 CFR § 483)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlagsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

// Staffing data types
interface StaffingMonth {
  month: string;
  year: number;
  hprd: number | null;
}

interface StaffingBaselines {
  sweetwater: number;
  nj: number;
  state_avg: number | null;
  national_avg: number | null;
  sweetwater_yearly?: Record<number, number>;
  nj_yearly?: Record<number, number>;
  sweetwater_monthly?: Record<string, number>;
  nj_monthly?: Record<string, number>;
}

const MonthlyTimeline: React.FC<MonthlyTimelineProps> = ({
  ccn,
  schemeId: _schemeId,
  startYear = 2019,
  endYear = 2025, // CMS data typically lags, hide current/future years
  hideChart = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timelineData, setTimelineData] = useState<MonthData[]>([]);
  const [summary, setSummary] = useState<{
    total_inspections: number;
    total_harm_citations: number;
    total_jeopardy_citations: number;
    total_penalties: number;
    total_fines: number;
  } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Staffing data state
  const [staffingData, setStaffingData] = useState<StaffingMonth[]>([]);
  const [staffingBaselines, setStaffingBaselines] = useState<StaffingBaselines | null>(null);

  // Dynamic facility color based on average HPRD vs case baselines
  const facilityLegendColor = React.useMemo(() => {
    const validPoints = staffingData.filter(d => d.hprd !== null && d.hprd > 0);
    if (validPoints.length === 0) return '#2e7d32';
    const avg = validPoints.reduce((sum, d) => sum + (d.hprd as number), 0) / validPoints.length;
    const sw = staffingBaselines?.sweetwater ?? 3.52;
    const nj = staffingBaselines?.nj ?? 3.17;
    if (avg < Math.min(sw, nj)) return '#d32f2f';
    if (avg > Math.max(sw, nj)) return '#2e7d32';
    return '#ed6c02';
  }, [staffingData, staffingBaselines]);

  // PDF Preview Dialog state
  const [pdfPreviewDialog, setPdfPreviewDialog] = useState<{
    open: boolean;
    inspection: InspectionEvent | null;
    url: string | null;
    reports: { url: string; filename: string; label: string; type: string }[];
  }>({
    open: false,
    inspection: null,
    url: null,
    reports: [],
  });

  // Highlighted citation from worst-citations strip click
  const [highlightedCitation, setHighlightedCitation] = useState<{ inspectionId: string; ftag: string } | null>(null);

  const handleWorstCitationClick = (inspectionId: string, ftag: string) => {
    setHighlightedCitation({ inspectionId, ftag });
    // Scroll to the specific citation row first, fall back to the inspection box
    setTimeout(() => {
      const rowEl = document.getElementById(`citation-${inspectionId}-${ftag}`);
      const inspEl = document.getElementById(`inspection-${inspectionId}`);
      const target = rowEl || inspEl;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
    // Clear highlight after 4 seconds
    setTimeout(() => setHighlightedCitation(null), 4000);
  };

  // Inspection marker popover state
  const [markerPopover, setMarkerPopover] = useState<{
    anchorEl: { x: number; y: number } | null;
    data: MonthData | null;
    month: string | null;
  }>({
    anchorEl: null,
    data: null,
    month: null,
  });

  const handleMarkerClick = (event: React.MouseEvent<SVGElement>, month: string, data: MonthData) => {
    const rect = (event.currentTarget as SVGElement).getBoundingClientRect();
    setMarkerPopover({
      anchorEl: { x: rect.left + rect.width / 2, y: rect.top },
      data,
      month,
    });
  };

  const handleMarkerPopoverClose = () => {
    setMarkerPopover({ anchorEl: null, data: null, month: null });
  };

  // Generate all months for the range
  const allMonths = generateAllMonths(startYear, endYear);

  // Create lookup for month data
  const monthDataMap = new Map<string, MonthData>();
  timelineData.forEach(d => monthDataMap.set(d.month, d));

  // Create lookup for staffing data
  const staffingMap = new Map<string, number | null>();
  staffingData.forEach(d => staffingMap.set(d.month, d.hprd));

  useEffect(() => {
    if (!ccn) return;

    let cancelled = false;

    const fetchTimeline = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch both timeline and staffing data in parallel
        const [timelineRes, staffingRes] = await Promise.all([
          api.get('/schemes/snf-inspections/monthly-timeline/', {
            params: { ccn, start_year: startYear, end_year: endYear },
          }),
          api.get('/schemes/snf-inspections/staffing-timeline/', {
            params: { ccn, start_year: startYear, end_year: endYear },
          }).catch(() => ({ data: { timeline: [], baselines: null } })), // Graceful fallback
        ]);

        if (cancelled) return;

        setTimelineData(timelineRes.data.timeline || []);
        setSummary(timelineRes.data.summary || null);
        setStaffingData(staffingRes.data.timeline || []);
        setStaffingBaselines(staffingRes.data.baselines || null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching monthly timeline:', err);
        setError('Failed to load timeline data');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTimeline();

    return () => {
      cancelled = true;
    };
  }, [ccn, startYear, endYear]);

  // Scroll to end (most recent) on load
  useEffect(() => {
    if (!loading && scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [loading]);

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // PDF Preview handlers
  const handlePreviewPdf = async (inspection: InspectionEvent) => {
    if (!inspection.has_report) return;

    // Open dialog immediately with loading state
    setPdfPreviewDialog({
      open: true,
      inspection,
      url: null,
      reports: [],
    });

    try {
      // Fetch report URL(s) from the backend using CCN + date + type
      const response = await api.get('/schemes/snf-inspections/report/', {
        params: { ccn, date: inspection.date, type: inspection.type },
      });

      const reports = response.data?.reports || [];
      setPdfPreviewDialog(prev => ({
        ...prev,
        url: response.data?.url || null,
        reports,
      }));
    } catch (err) {
      console.error('Error fetching report URL:', err);
      setPdfPreviewDialog({ open: false, inspection: null, url: null, reports: [] });
    }
  };

  const handleClosePdfPreview = () => {
    setPdfPreviewDialog({
      open: false,
      inspection: null,
      url: null,
      reports: [],
    });
  };

  const handleDownloadPdf = async (inspection: InspectionEvent) => {
    if (!inspection.has_report) return;
    try {
      const response = await api.get('/schemes/snf-inspections/report/', {
        params: { ccn, date: inspection.date, type: inspection.type, download: 'true' },
      });
      if (response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Error downloading report:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box py={2}>
        <Typography color="error" variant="body2">{error}</Typography>
      </Box>
    );
  }

  // Group months by year for rendering
  const years = Array.from(new Set(allMonths.map(m => m.split('-')[0]))).sort();

  return (
    <Box sx={{ mb: 3 }}>
      {!hideChart && (<>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Staffing & Inspections ({startYear}–{endYear})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Green line shows total nursing hours per patient day. Circles show inspections - click for details. Size = citations, color = severity.
          </Typography>
        </Box>
        {summary && (
          <Box display="flex" gap={2} alignItems="center">
            <Chip
              size="small"
              label={`${summary.total_inspections} inspections`}
              sx={{ fontSize: '0.7rem' }}
            />
            {summary.total_harm_citations > 0 && (
              <Chip
                size="small"
                label={`${summary.total_harm_citations} harm`}
                color="error"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
            {summary.total_fines > 0 && (
              <Chip
                size="small"
                label={formatMoney(summary.total_fines)}
                color="warning"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Timeline container with scroll */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'white',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          p: 1,
        }}
      >
        {/* Left scroll button */}
        <IconButton size="small" onClick={() => handleScroll('left')} sx={{ mr: 0.5 }}>
          <ChevronLeft />
        </IconButton>

        {/* Scrollable timeline */}
        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            '&::-webkit-scrollbar': {
              height: 6,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'grey.100',
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'grey.400',
              borderRadius: 3,
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Year labels */}
            <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'grey.200', mb: 0.5 }}>
              {years.map(year => (
                <Box
                  key={year}
                  sx={{
                    width: CELL_WIDTH * 12, // 12 months per year
                    minWidth: CELL_WIDTH * 12,
                    textAlign: 'center',
                    borderLeft: '2px solid',
                    borderLeftColor: 'grey.400',
                  }}
                >
                  <Typography variant="body2" fontWeight={700} color="text.primary">
                    {year}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Unified staffing + inspection chart */}
            <Box sx={{ position: 'relative', height: 180 }}>
              {/* Y-axis label */}
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  left: -40,
                  top: '40%',
                  transform: 'translateY(-50%) rotate(-90deg)',
                  fontSize: '0.6rem',
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                HPRD
              </Typography>

              {/* SVG chart */}
              <svg
                width={allMonths.length * CELL_WIDTH}
                height={180}
                style={{ display: 'block' }}
              >
                {(() => {
                  // Use monthly baselines for chart lines, yearly for dot comparisons
                  const swMonthly = staffingBaselines?.sweetwater_monthly || {};
                  const njMonthly = staffingBaselines?.nj_monthly || {};
                  const swYearly = staffingBaselines?.sweetwater_yearly || {};
                  const njYearly = staffingBaselines?.nj_yearly || {};

                  // Helper to get year from month string ("2022-01" -> 2022)
                  const getYear = (month: string) => parseInt(month.split('-')[0], 10);

                  // Chart dimensions - leave room at top for markers
                  const chartTop = 50;
                  const chartHeight = 100;
                  const inspectionZoneY = chartTop + chartHeight + 15; // Below staffing chart

                  // Calculate Y scaling for staffing
                  const validHprd = staffingData.filter(d => d.hprd !== null).map(d => d.hprd as number);
                  const swValues = Object.values(swMonthly);
                  const njValues = Object.values(njMonthly);
                  const allStaffingValues = [
                    ...validHprd,
                    ...swValues,
                    ...njValues,
                    ...(staffingBaselines?.state_avg ? [staffingBaselines.state_avg] : []),
                    ...(staffingBaselines?.national_avg ? [staffingBaselines.national_avg] : []),
                  ];

                  const minVal = allStaffingValues.length > 0 ? Math.min(...allStaffingValues) * 0.85 : 1;
                  const maxVal = allStaffingValues.length > 0 ? Math.max(...allStaffingValues) * 1.1 : 5;
                  const range = maxVal - minVal || 1;

                  const scaleY = (val: number) => chartTop + chartHeight - ((val - minVal) / range) * chartHeight;

                  // Build facility staffing points
                  const staffingPoints: { x: number; y: number; month: string; hprd: number; year: number }[] = [];
                  allMonths.forEach((month, i) => {
                    const hprd = staffingMap.get(month);
                    if (hprd !== null && hprd !== undefined) {
                      staffingPoints.push({
                        x: i * CELL_WIDTH + CELL_WIDTH / 2,
                        y: scaleY(hprd),
                        month,
                        hprd,
                        year: getYear(month),
                      });
                    }
                  });

                  // Dynamic facility color based on average vs baselines
                  const facAvgHprd = staffingPoints.length > 0
                    ? staffingPoints.reduce((sum, p) => sum + p.hprd, 0) / staffingPoints.length
                    : 0;
                  const swBaseline = staffingBaselines?.sweetwater ?? 3.52;
                  const njBaseline = staffingBaselines?.nj ?? 3.17;
                  const facilityLineColor = facAvgHprd < Math.min(swBaseline, njBaseline)
                    ? '#d32f2f' : facAvgHprd > Math.max(swBaseline, njBaseline)
                    ? '#2e7d32' : '#ed6c02';

                  const linePath = staffingPoints.length > 1
                    ? staffingPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
                    : '';

                  // Build Sweetwater path using monthly baselines for smooth trend line
                  const swPoints: { x: number; y: number }[] = [];
                  allMonths.forEach((month, i) => {
                    const val = swMonthly[month];
                    if (val !== undefined) {
                      swPoints.push({ x: i * CELL_WIDTH + CELL_WIDTH / 2, y: scaleY(val) });
                    }
                  });
                  const swPath = swPoints.length > 1
                    ? swPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
                    : '';

                  // Build NJ path using monthly baselines for smooth trend line
                  const njPoints: { x: number; y: number }[] = [];
                  allMonths.forEach((month, i) => {
                    const val = njMonthly[month];
                    if (val !== undefined) {
                      njPoints.push({ x: i * CELL_WIDTH + CELL_WIDTH / 2, y: scaleY(val) });
                    }
                  });
                  const njPath = njPoints.length > 1
                    ? njPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
                    : '';

                  // Area fill path
                  const areaPath = staffingPoints.length > 1
                    ? `${linePath} L ${staffingPoints[staffingPoints.length - 1].x} ${chartTop + chartHeight} L ${staffingPoints[0].x} ${chartTop + chartHeight} Z`
                    : '';

                  // Build inspection markers - positioned at staffing line Y or fixed position
                  const inspectionMarkers: {
                    x: number;
                    y: number;
                    month: string;
                    data: MonthData;
                    hasStaffingData: boolean;
                  }[] = [];

                  allMonths.forEach((month, i) => {
                    const monthData = monthDataMap.get(month);
                    if (monthData && (monthData.inspections.length > 0 || monthData.penalties.length > 0)) {
                      const staffingPoint = staffingPoints.find(p => p.month === month);
                      inspectionMarkers.push({
                        x: i * CELL_WIDTH + CELL_WIDTH / 2,
                        y: staffingPoint ? staffingPoint.y : inspectionZoneY,
                        month,
                        data: monthData,
                        hasStaffingData: !!staffingPoint,
                      });
                    }
                  });

                  // Determine marker size based on citation count (min 18, max 32 for better visibility)
                  const getMarkerSize = (citations: number) => Math.min(32, Math.max(18, 14 + citations * 1.2));

                  // Determine marker color based on severity
                  const getMarkerColor = (data: MonthData) => {
                    if (data.totals.jeopardy_citations > 0) return '#7b1fa2'; // Purple
                    if (data.totals.harm_citations > 0) return '#d32f2f'; // Red
                    return '#1976d2'; // Blue
                  };

                  // Get lighter version of color for outer ring
                  const getMarkerRingColor = (data: MonthData) => {
                    if (data.totals.jeopardy_citations > 0) return '#e1bee7'; // Light purple
                    if (data.totals.harm_citations > 0) return '#ffcdd2'; // Light red
                    return '#bbdefb'; // Light blue
                  };

                  return (
                    <>
                      {/* Gradient definition */}
                      <defs>
                        <linearGradient id="staffingGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor={facilityLineColor} stopOpacity={0.4} />
                          <stop offset="100%" stopColor={facilityLineColor} stopOpacity={0.05} />
                        </linearGradient>
                        {/* Drop shadow for inspection markers */}
                        <filter id="markerShadow" x="-50%" y="-50%" width="200%" height="200%">
                          <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodOpacity="0.3" />
                        </filter>
                      </defs>

                      {/* Vertical highlight columns for inspection months */}
                      {inspectionMarkers.map((marker, i) => {
                        const color = getMarkerColor(marker.data);
                        const hasPenalty = marker.data.totals.total_fines > 0;
                        return (
                          <rect
                            key={`highlight-${i}`}
                            x={marker.x - CELL_WIDTH / 2}
                            y={0}
                            width={CELL_WIDTH}
                            height={180}
                            fill={color}
                            opacity={hasPenalty ? 0.12 : 0.08}
                          />
                        );
                      })}

                      {/* Year divider lines */}
                      {years.map((year, idx) => (
                        <line
                          key={year}
                          x1={idx * 12 * CELL_WIDTH}
                          y1={chartTop}
                          x2={idx * 12 * CELL_WIDTH}
                          y2={chartTop + chartHeight}
                          stroke="#e0e0e0"
                          strokeWidth={1}
                          strokeDasharray="4,4"
                        />
                      ))}

                      {/* Sweetwater baseline line */}
                      {swPath && (
                        <path d={swPath} fill="none" stroke="#1565c0" strokeWidth={1.5} strokeOpacity={0.5} strokeDasharray="4,2" />
                      )}

                      {/* NJ baseline line */}
                      {njPath && (
                        <path d={njPath} fill="none" stroke="#7b1fa2" strokeWidth={1.5} strokeOpacity={0.5} strokeDasharray="4,2" />
                      )}

                      {/* Staffing area fill */}
                      {areaPath && <path d={areaPath} fill="url(#staffingGradient)" />}

                      {/* Staffing line */}
                      {linePath && (
                        <path d={linePath} fill="none" stroke={facilityLineColor} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
                      )}

                      {/* Small dots on staffing line (no inspection) */}
                      {staffingPoints.map((p, i) => {
                        const hasInspection = inspectionMarkers.some(m => m.month === p.month);
                        if (hasInspection) return null;

                        const njVal = njYearly[p.year] ?? staffingBaselines?.nj ?? 3.17;
                        const swVal = swYearly[p.year] ?? staffingBaselines?.sweetwater ?? 3.52;
                        const isBelowNJ = p.hprd < njVal;
                        const isBelowSweetwater = p.hprd < swVal;

                        return (
                          <Tooltip key={i} title={`${p.month}: ${p.hprd.toFixed(2)} hrs/patient/day`}>
                            <circle
                              cx={p.x}
                              cy={p.y}
                              r={3}
                              fill={isBelowNJ ? '#d32f2f' : isBelowSweetwater ? '#ed6c02' : '#2e7d32'}
                              stroke="white"
                              strokeWidth={1}
                              style={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        );
                      })}

                      {/* Inspection event markers */}
                      {inspectionMarkers.map((marker, i) => {
                        const size = getMarkerSize(marker.data.totals.total_citations);
                        const color = getMarkerColor(marker.data);
                        const ringColor = getMarkerRingColor(marker.data);
                        const hasPenalty = marker.data.totals.total_fines > 0;
                        const staffingPoint = staffingPoints.find(p => p.month === marker.month);

                        // Position marker slightly above the line for visibility
                        const markerY = marker.y - 8;

                        // Tooltip content
                        const tooltipLines = [
                          `${marker.month}`,
                          `${marker.data.totals.total_citations} citations`,
                        ];
                        if (marker.data.totals.harm_citations > 0) {
                          tooltipLines.push(`${marker.data.totals.harm_citations} harm`);
                        }
                        if (marker.data.totals.jeopardy_citations > 0) {
                          tooltipLines.push(`${marker.data.totals.jeopardy_citations} jeopardy`);
                        }
                        if (staffingPoint) {
                          tooltipLines.push(`HPRD: ${staffingPoint.hprd.toFixed(2)}`);
                        }
                        if (hasPenalty) {
                          tooltipLines.push(`Fine: ${formatMoney(marker.data.totals.total_fines)}`);
                        }

                        return (
                          <g key={i}>
                            {/* Outer glow ring for visibility */}
                            <circle
                              cx={marker.x}
                              cy={markerY}
                              r={size / 2 + 6}
                              fill={ringColor}
                              opacity={0.5}
                            />

                            {/* Connector line to staffing point */}
                            {marker.hasStaffingData && (
                              <line
                                x1={marker.x}
                                y1={markerY + size / 2}
                                x2={marker.x}
                                y2={marker.y}
                                stroke={color}
                                strokeWidth={2}
                                strokeDasharray="2,2"
                                opacity={0.6}
                              />
                            )}

                            {/* Inspection marker - clickable */}
                            <g
                              style={{ cursor: 'pointer' }}
                              onClick={(e) => handleMarkerClick(e, marker.month, marker.data)}
                            >
                              {/* Main circle */}
                              <circle
                                cx={marker.x}
                                cy={markerY}
                                r={size / 2}
                                fill={color}
                                stroke="white"
                                strokeWidth={3}
                                filter="url(#markerShadow)"
                              />
                              {/* Citation count inside marker */}
                              <text
                                x={marker.x}
                                y={markerY + 1}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={size > 24 ? 11 : 9}
                                fontWeight={700}
                                fill="white"
                                style={{ pointerEvents: 'none' }}
                              >
                                {marker.data.totals.total_citations}
                              </text>
                            </g>

                            {/* Penalty badge - positioned to the side */}
                            {hasPenalty && (
                              <g
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => handleMarkerClick(e, marker.month, marker.data)}
                              >
                                <rect
                                  x={marker.x + size / 2 - 2}
                                  y={markerY - 8}
                                  width={20}
                                  height={16}
                                  rx={3}
                                  fill="#ed6c02"
                                  stroke="white"
                                  strokeWidth={2}
                                />
                                <text
                                  x={marker.x + size / 2 + 8}
                                  y={markerY + 1}
                                  textAnchor="middle"
                                  fontSize={9}
                                  fontWeight={700}
                                  fill="white"
                                  style={{ pointerEvents: 'none' }}
                                >
                                  $
                                </text>
                              </g>
                            )}
                          </g>
                        );
                      })}

                      {/* Legend labels (at top right of chart) */}
                      <text x={allMonths.length * CELL_WIDTH - 5} y={15} textAnchor="end" fontSize={9} fill={facilityLineColor} fontWeight={600}>
                        ─ This Facility
                      </text>
                      <text x={allMonths.length * CELL_WIDTH - 5} y={28} textAnchor="end" fontSize={9} fill="#1565c0" fontWeight={600}>
                        ╌ Sweetwater
                      </text>
                      <text x={allMonths.length * CELL_WIDTH - 5} y={41} textAnchor="end" fontSize={9} fill="#7b1fa2" fontWeight={600}>
                        ╌ Deptford
                      </text>
                    </>
                  );
                })()}
              </svg>
            </Box>

            {/* Month labels (every month) */}
            <Box sx={{ display: 'flex', mt: 0.5 }}>
              {allMonths.map((month) => {
                return (
                  <Box
                    key={month}
                    sx={{
                      width: CELL_WIDTH,
                      minWidth: CELL_WIDTH,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {getMonthLabel(month)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Right scroll button */}
        <IconButton size="small" onClick={() => handleScroll('right')} sx={{ ml: 0.5 }}>
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Inspection marker detail popover */}
      <Popover
        open={Boolean(markerPopover.anchorEl)}
        onClose={handleMarkerPopoverClose}
        anchorReference="anchorPosition"
        anchorPosition={markerPopover.anchorEl ? { top: markerPopover.anchorEl.y, left: markerPopover.anchorEl.x } : undefined}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{ sx: { borderRadius: 2, maxWidth: 400 } }}
      >
        {markerPopover.data && (
          <Box sx={{ p: 2, minWidth: 300 }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
              {getMonthLabel(markerPopover.month || '')} {markerPopover.month?.split('-')[0]}
            </Typography>

            {/* Inspections */}
            {markerPopover.data.inspections.map((insp) => (
              <Box
                key={insp.id}
                sx={{
                  p: 1.5,
                  mb: 1,
                  backgroundColor: insp.type === 'COMBINED' ? '#ede7f6' : insp.type === 'HEALTH' ? 'primary.50' : 'warning.50',
                  borderRadius: 1,
                  borderLeft: '3px solid',
                  borderLeftColor: insp.type === 'COMBINED' ? '#5e35b1' : insp.type === 'HEALTH' ? 'primary.main' : 'warning.main',
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  {insp.type === 'COMBINED' ? (
                    <HealthIcon fontSize="small" sx={{ color: '#5e35b1' }} />
                  ) : insp.type === 'HEALTH' ? (
                    <HealthIcon fontSize="small" color="primary" />
                  ) : (
                    <ComplaintIcon fontSize="small" color="warning" />
                  )}
                  <Typography variant="body2" fontWeight={600}>
                    {insp.type === 'COMBINED' ? 'Combined Inspection' : insp.type === 'HEALTH' ? 'Health Inspection' : 'Complaint Inspection'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {new Date(insp.date).toLocaleDateString()}
                </Typography>

                {/* Citation breakdown */}
                <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                  <Chip size="small" label={`${insp.total_citations} citations`} sx={{ fontSize: '0.7rem', height: 20 }} />
                  {insp.harm_citations > 0 && (
                    <Chip size="small" label={`${insp.harm_citations} harm`} color="error" sx={{ fontSize: '0.7rem', height: 20 }} />
                  )}
                  {insp.jeopardy_citations > 0 && (
                    <Chip size="small" label={`${insp.jeopardy_citations} jeopardy`} sx={{ fontSize: '0.7rem', height: 20, backgroundColor: '#7b1fa2', color: 'white' }} />
                  )}
                </Box>

                {/* Core staffing F-tags */}
                {insp.core_staffing_ftags.length > 0 && (
                  <Box mt={1}>
                    <Typography variant="caption" color="error.main" fontWeight={600}>
                      Core Staffing: {insp.core_staffing_ftags.join(', ')}
                    </Typography>
                  </Box>
                )}

                {/* Citation details table */}
                {insp.citations && insp.citations.length > 0 && (
                  <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider', maxHeight: 200, overflowY: 'auto' }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Citations:
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 600, py: 0.25, px: 0.5, fontSize: '0.65rem', width: 55 }}>F-Tag</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 0.25, px: 0.5, fontSize: '0.65rem' }}>Description</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 0.25, px: 0.5, fontSize: '0.65rem', width: 40 }} align="center">Sev</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {insp.citations.map((citation, citIdx) => (
                          <TableRow key={`${citation.ftag}-${citIdx}`} sx={{ backgroundColor: getSeverityBgColor(citation.severity) }}>
                            <TableCell sx={{ py: 0.25, px: 0.5 }}>
                              <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem', color: getSeverityColor(citation.severity) }}>
                                {citation.ftag}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 0.25, px: 0.5 }}>
                              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {citation.description || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.25, px: 0.5 }}>
                              <Chip
                                size="small"
                                label={citation.severity}
                                sx={{ fontSize: '0.6rem', height: 16, backgroundColor: getSeverityBgColor(citation.severity), color: getSeverityColor(citation.severity), fontWeight: 600, border: `1px solid ${getSeverityColor(citation.severity)}` }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            ))}

            {/* Penalties */}
            {markerPopover.data.penalties.filter(p => p.fine_amount > 0).length > 0 && (
              <Box sx={{ p: 1.5, backgroundColor: 'warning.50', borderRadius: 1, borderLeft: '3px solid', borderLeftColor: 'warning.main' }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <PenaltyIcon fontSize="small" color="warning" />
                  <Typography variant="body2" fontWeight={600}>
                    Civil Money Penalty
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={700} color="warning.dark">
                  {formatMoney(markerPopover.data.totals.total_fines)}
                </Typography>
              </Box>
            )}

            {/* Payment denials */}
            {markerPopover.data.penalties.filter(p => p.payment_denial_days && p.payment_denial_days > 0).map((denial, idx) => (
              <Box key={idx} sx={{ p: 1.5, mt: 1, backgroundColor: '#fce4ec', borderRadius: 1, border: '2px solid #c2185b' }}>
                <Typography variant="body2" fontWeight={700} color="#880e4f">
                  PAYMENT DENIAL - {denial.payment_denial_days} Days
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  CMS stopped payments for new admissions
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Popover>

      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 2,
          py: 1.5,
          px: 2,
          justifyContent: 'center',
          flexWrap: 'wrap',
          backgroundColor: 'grey.50',
          borderRadius: 1,
        }}
      >
        {/* Staffing lines section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 2, borderRight: '1px solid', borderColor: 'grey.300' }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Staffing:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 20, height: 3, backgroundColor: facilityLegendColor, borderRadius: 1 }} />
            <Typography variant="caption" color="text.secondary">This Facility</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 20, height: 2, backgroundColor: '#1565c0', borderRadius: 1, opacity: 0.6 }} />
            <Typography variant="caption" color="text.secondary">Sweetwater</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 20, height: 2, backgroundColor: '#7b1fa2', borderRadius: 1, opacity: 0.6 }} />
            <Typography variant="caption" color="text.secondary">Deptford</Typography>
          </Box>
        </Box>

        {/* Inspection markers section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Inspections:
          </Typography>
          <Tooltip title="Immediate jeopardy to resident health/safety - most severe">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#7b1fa2', boxShadow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '0.55rem', color: 'white', fontWeight: 700 }}>3</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Jeopardy</Typography>
            </Box>
          </Tooltip>
          <Tooltip title="Actual harm occurred to residents">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#d32f2f', boxShadow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '0.55rem', color: 'white', fontWeight: 700 }}>3</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Harm</Typography>
            </Box>
          </Tooltip>
          <Tooltip title="Standard inspection with no harm findings">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#1976d2', boxShadow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '0.55rem', color: 'white', fontWeight: 700 }}>3</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Standard</Typography>
            </Box>
          </Tooltip>
          <Tooltip title="Civil money penalty imposed by CMS">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
              <Box sx={{ width: 14, height: 12, borderRadius: 0.5, backgroundColor: '#ed6c02', boxShadow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '0.6rem', color: 'white', fontWeight: 700 }}>$</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Penalty</Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      </>)}

      {/* Worst Citations Strip + Severity Legend — shown regardless of hideChart */}
      {timelineData.length > 0 && (
        <>
          <SeverityLegend />
          <WorstCitationsStrip
            timelineData={timelineData}
            startYear={startYear}
            endYear={endYear}
            onCitationClick={handleWorstCitationClick}
          />
        </>
      )}

      {/* Vertical Timeline - Detailed view */}
      {timelineData.length > 0 && (
        <Box>
          <Box
            sx={{
              p: 2,
              backgroundColor: 'white',
              borderRadius: timelineData.some(m => m.inspections.some(i =>
                i.citations?.some(c =>
                  WORST_CITATION_SEVERITIES.has(c.severity) ||
                  c.is_understaffing_core || c.is_understaffing_indicator || c.is_staffing ||
                  c.is_abuse
                )
              )) ? '0 0 8px 8px' : 2,
              border: '1px solid',
              borderColor: 'divider',
              minHeight: 600,
              maxHeight: 800,
              overflowY: 'auto',
            }}
          >
          {/* Group and display events in reverse chronological order */}
          {[...timelineData].reverse().map((monthData) => (
            <Box
              key={monthData.month}
              sx={{
                display: 'flex',
                gap: 2,
                mb: 2,
                pb: 2,
                borderBottom: '1px solid',
                borderColor: 'grey.100',
                '&:last-child': { borderBottom: 'none', mb: 0, pb: 0 },
              }}
            >
              {/* Date column - fixed width on the left, sticky */}
              <Box
                sx={{
                  width: 90,
                  minWidth: 90,
                  pt: 0.5,
                  position: 'sticky',
                  top: 0,
                  alignSelf: 'flex-start',
                  backgroundColor: 'white',
                  zIndex: 1,
                }}
              >
                <Typography variant="body2" fontWeight={700} color="text.primary">
                  {getMonthLabel(monthData.month)} {monthData.year}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                  {monthData.totals.harm_citations > 0 && (
                    <Chip size="small" label={`${monthData.totals.harm_citations} harm`} color="error" sx={{ height: 18, fontSize: '0.6rem' }} />
                  )}
                  {monthData.totals.total_fines > 0 && (
                    <Chip size="small" label={formatMoney(monthData.totals.total_fines)} color="warning" sx={{ height: 18, fontSize: '0.6rem' }} />
                  )}
                </Box>
              </Box>

              {/* Events column - flex grow */}
              <Box sx={{ flex: 1, borderLeft: '2px solid', borderColor: 'grey.300', pl: 2 }}>
                {/* Inspections */}
                {monthData.inspections.map((insp) => {
                  const inspKey = insp.id || insp.date;
                  return (
                  <Box
                    key={inspKey}
                    id={`inspection-${inspKey}`}
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      backgroundColor: insp.harm_citations > 0 ? '#ffebee' : insp.type === 'COMBINED' ? '#ede7f6' : insp.type === 'HEALTH' ? '#e3f2fd' : '#fff3e0',
                      borderRadius: 1,
                      borderLeft: '3px solid',
                      borderLeftColor: insp.harm_citations > 0 ? '#d32f2f' : insp.type === 'COMBINED' ? '#5e35b1' : insp.type === 'HEALTH' ? '#1976d2' : '#ed6c02',
                      ...(highlightedCitation?.inspectionId === inspKey && {
                        outline: '2px solid #1976d2',
                        outlineOffset: 2,
                        animation: 'highlightPulse 1.5s ease-in-out 2',
                        '@keyframes highlightPulse': {
                          '0%, 100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
                          '50%': { boxShadow: '0 0 12px 4px rgba(25, 118, 210, 0.3)' },
                        },
                      }),
                      transition: 'outline 0.3s, box-shadow 0.3s',
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {insp.type === 'COMBINED' ? (
                          <HealthIcon fontSize="small" sx={{ color: insp.harm_citations > 0 ? '#d32f2f' : '#5e35b1' }} />
                        ) : insp.type === 'HEALTH' ? (
                          <HealthIcon fontSize="small" sx={{ color: insp.harm_citations > 0 ? '#d32f2f' : '#1976d2' }} />
                        ) : (
                          <ComplaintIcon fontSize="small" sx={{ color: '#ed6c02' }} />
                        )}
                        <Typography variant="body2" fontWeight={600}>
                          {insp.type === 'COMBINED' ? 'Combined Inspection' : insp.type === 'HEALTH' ? 'Health Inspection' : 'Complaint Inspection'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(insp.date).toLocaleDateString()}
                      </Typography>
                    </Box>

                    {/* Summary chips */}
                    <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                      <Chip size="small" label={`${insp.total_citations} citations`} sx={{ height: 18, fontSize: '0.65rem' }} />
                      {insp.harm_citations > 0 && (
                        <Chip size="small" label={`${insp.harm_citations} harm`} color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                      {insp.jeopardy_citations > 0 && (
                        <Chip size="small" label={`${insp.jeopardy_citations} jeopardy`} sx={{ height: 18, fontSize: '0.65rem', backgroundColor: '#7b1fa2', color: 'white' }} />
                      )}
                      {insp.has_staffing_citation && (
                        <Chip size="small" label="Staffing" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>

                    {/* Citations table */}
                    {insp.citations && insp.citations.length > 0 && (
                      <Table size="small" sx={{ mt: 1 }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'grey.100' }}>
                            <TableCell sx={{ fontWeight: 600, py: 0.5, px: 1, width: 70 }}>F-Tag</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 0.5, px: 1 }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 0.5, px: 1, width: 55 }} align="center">Severity</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 0.5, px: 1, width: 120 }} align="center">Flags</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {insp.citations.map((citation, idx) => {
                            const isHighlightedRow =
                              highlightedCitation?.inspectionId === inspKey &&
                              highlightedCitation?.ftag === citation.ftag;
                            return (
                            <TableRow
                              key={`${citation.ftag}-${idx}`}
                              id={`citation-${inspKey}-${citation.ftag}`}
                              sx={{
                                backgroundColor: isHighlightedRow
                                  ? '#bbdefb'
                                  : getSeverityBgColor(citation.severity),
                                '&:hover': { opacity: 0.9 },
                                ...(isHighlightedRow && {
                                  outline: '2px solid #1976d2',
                                  animation: 'rowPulse 1s ease-in-out 3',
                                  '@keyframes rowPulse': {
                                    '0%, 100%': { backgroundColor: '#bbdefb' },
                                    '50%': { backgroundColor: '#90caf9' },
                                  },
                                }),
                              }}
                            >
                              <TableCell sx={{ py: 0.5, px: 1 }}>
                                <Tooltip title={citation.category || 'Unknown category'}>
                                  <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ color: getSeverityColor(citation.severity) }}
                                  >
                                    {citation.ftag}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={{ py: 0.5, px: 1 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {citation.description || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                                <Tooltip title={SEVERITY_INFO[citation.severity]?.label || citation.severity}>
                                  <Chip
                                    size="small"
                                    label={citation.severity}
                                    sx={{
                                      fontSize: '0.65rem',
                                      height: 18,
                                      backgroundColor: getSeverityBgColor(citation.severity),
                                      color: getSeverityColor(citation.severity),
                                      fontWeight: 600,
                                      border: `1px solid ${getSeverityColor(citation.severity)}`,
                                    }}
                                  />
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                                <Box display="flex" gap={0.5} justifyContent="center" flexWrap="wrap">
                                  {citation.is_understaffing_core && (() => {
                                    const isJeopardy = ['J', 'K', 'L'].includes(citation.severity);
                                    const isHarm = ['G', 'H', 'I'].includes(citation.severity);
                                    const chipBg = isJeopardy ? '#7b1fa2' : isHarm ? '#d32f2f' : '#ed6c02';
                                    return (
                                      <Tooltip title="Core understaffing citation">
                                        <Chip size="small" label="Understaffing" sx={{ fontSize: '0.6rem', height: 16, backgroundColor: chipBg, color: 'white' }} />
                                      </Tooltip>
                                    );
                                  })()}
                                  {!citation.is_understaffing_core && (citation.is_understaffing_indicator || citation.is_staffing) && (
                                    <Tooltip title="Staffing-related citation">
                                      <Chip size="small" label="Staffing-Related" variant="outlined" sx={{ fontSize: '0.6rem', height: 16, borderColor: '#ef6c00', color: '#ef6c00' }} />
                                    </Tooltip>
                                  )}
                                  {citation.is_abuse && (
                                    <Tooltip title="Abuse/Neglect citation (F600-F610)">
                                      <Chip size="small" label="Abuse" color="error" variant="outlined" sx={{ fontSize: '0.6rem', height: 16 }} />
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}

                    {/* View Report button */}
                    {insp.has_report && (
                      <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'grey.200' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PreviewIcon />}
                          onClick={() => handlePreviewPdf(insp)}
                          sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                        >
                          View Inspection Report
                        </Button>
                      </Box>
                    )}
                  </Box>
                  );
                })}

                {/* Penalties - grouped by date */}
                {(() => {
                  // Separate payment denials from fines
                  const paymentDenials = monthData.penalties.filter(p => p.payment_denial_days && p.payment_denial_days > 0);
                  const fines = monthData.penalties.filter(p => p.fine_amount > 0);

                  // Group fines by date
                  const finesByDate = fines.reduce((acc, fine) => {
                    const dateKey = fine.date;
                    if (!acc[dateKey]) acc[dateKey] = [];
                    acc[dateKey].push(fine);
                    return acc;
                  }, {} as Record<string, typeof fines>);

                  return (
                    <>
                      {/* Payment Denials - PROMINENT display */}
                      {paymentDenials.map((denial, idx) => (
                        <Tooltip
                          key={`denial-${denial.date}-${idx}`}
                          title="Denial of Payment for New Admissions (DPNA): CMS will not pay for any new Medicare/Medicaid residents admitted after this date. Existing residents are not affected. Mandatory when a facility fails to correct deficiencies within 3 months. If not corrected within 6 months, the facility is terminated from Medicare/Medicaid."
                        >
                          <Box
                            sx={{
                              mb: 1.5,
                              p: 2,
                              backgroundColor: '#fce4ec',
                              borderRadius: 1,
                              border: '2px solid',
                              borderColor: '#c2185b',
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            {/* Severity stripe */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 4,
                                backgroundColor: '#c2185b',
                              }}
                            />
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    backgroundColor: '#c2185b',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Typography sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 700 }}>!</Typography>
                                </Box>
                                <Typography variant="body2" fontWeight={700} color="#880e4f">
                                  PAYMENT DENIAL
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                label="Severe Sanction"
                                sx={{
                                  backgroundColor: '#c2185b',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.65rem',
                                }}
                              />
                            </Box>
                            <Box sx={{ pl: 4.5 }}>
                              <Typography variant="h6" fontWeight={700} color="#880e4f">
                                {denial.payment_denial_days} Days
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                CMS stopped payments for new admissions
                              </Typography>
                              {denial.payment_denial_start && (
                                <Typography variant="caption" color="text.secondary">
                                  Starting {new Date(denial.payment_denial_start).toLocaleDateString()}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Tooltip>
                      ))}

                      {/* Fines - grouped by date (compact single-line display) */}
                      {Object.keys(finesByDate).length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                          Fines imposed by CMS
                        </Typography>
                      )}
                      {Object.entries(finesByDate).map(([date, dateFines]) => {
                        const totalAmount = dateFines.reduce((sum, f) => sum + f.fine_amount, 0);
                        const fineCount = dateFines.length;

                        return (
                          <Tooltip
                            key={`fines-${date}`}
                            title={fineCount > 1 ? `${fineCount} penalties: ${dateFines.map(f => formatMoney(f.fine_amount)).join(', ')}` : ''}
                            arrow
                            placement="top"
                          >
                            <Box
                              sx={{
                                mb: 1,
                                py: 0.75,
                                px: 1.5,
                                backgroundColor: '#fff3e0',
                                borderRadius: 1,
                                borderLeft: '3px solid',
                                borderLeftColor: '#ed6c02',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <PenaltyIcon sx={{ fontSize: 16 }} color="warning" />
                                <Typography variant="body2" fontWeight={600} color="warning.dark">
                                  {formatMoney(totalAmount)}
                                </Typography>
                                {fineCount > 1 && (
                                  <Typography variant="caption" color="text.secondary">
                                    ({fineCount} penalties)
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(date).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </>
                  );
                })()}
              </Box>
            </Box>
          ))}
          </Box>
        </Box>
      )}

      {/* PDF Preview Dialog */}
      <Dialog
        open={pdfPreviewDialog.open}
        onClose={handleClosePdfPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            height: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 2,
            px: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <DocumentIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {pdfPreviewDialog.inspection
                ? `Inspection Report - ${formatDate(pdfPreviewDialog.inspection.date)}`
                : 'Inspection Report'}
            </Typography>
            {pdfPreviewDialog.reports.length > 1 ? (
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                {pdfPreviewDialog.reports.map((rpt) => (
                  <Chip
                    key={rpt.type}
                    size="small"
                    label={rpt.label}
                    color={rpt.type === 'COMPLAINT' ? 'warning' : 'primary'}
                    variant={pdfPreviewDialog.url === rpt.url ? 'filled' : 'outlined'}
                    onClick={() => setPdfPreviewDialog(prev => ({ ...prev, url: rpt.url }))}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            ) : pdfPreviewDialog.inspection && (
              <Chip
                size="small"
                label={pdfPreviewDialog.inspection.type === 'COMBINED' ? 'Combined' : pdfPreviewDialog.inspection.type === 'HEALTH' ? 'Health' : 'Complaint'}
                color={pdfPreviewDialog.inspection.type === 'COMPLAINT' ? 'warning' : 'primary'}
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <IconButton onClick={handleClosePdfPreview} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 'calc(90vh - 120px)', overflow: 'hidden' }}>
          {pdfPreviewDialog.url ? (
            <Box
              component="iframe"
              src={pdfPreviewDialog.url}
              sx={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: '#525659',
              }}
              title="Inspection Report PDF"
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
              }}
            >
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Loading PDF...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => pdfPreviewDialog.inspection && handleDownloadPdf(pdfPreviewDialog.inspection)}
            startIcon={<DownloadIcon />}
            variant="contained"
          >
            Download
          </Button>
          <Button onClick={handleClosePdfPreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonthlyTimeline;
